// Evidence stack — two collapsible BOL evidence groups for the Chronology page:
// US IMPORT (COO confirmation, logistics) + API ORIGIN SIGNAL (chemistry). TSX
// port of the mockup's EvidenceStack/EvidenceGroup/EvidencePanel/tables.
import React from 'react';
import { C, MONO, fmt, fmtUsd, TIER_COLOR } from './chronologyTokens';
import { Flag } from './chronologyFlags';
import { EvidenceGroups, EvidencePanelModel, EvidenceRow, ChronoApiOrigin } from './chronologyAdapters';

const GROUP_COO = { label: 'US IMPORT', desc: 'Where the finished drug shipped from — logistics layer (confirms COO)', color: C.mute };
const GROUP_API = { label: 'API ORIGIN SIGNAL', desc: 'Where the active ingredient was synthesised — chemistry layer', color: C.red };

// ── Country chip (used in the signal summary) ──────────────────────────────
function CountryChip({ iso, name, tier, share, shareBasis, color }: { iso: string | null; name: string; tier?: string | null; share?: number | null; shareBasis?: string | null; color?: string }) {
  const c = color || (tier ? TIER_COLOR[tier.toUpperCase()] : null) || C.ink;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: C.bg, border: `1px solid ${c}55` }}>
      <Flag iso={iso} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {iso && <div style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: c, letterSpacing: '0.1em' }}>{iso}</div>}
        <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, lineHeight: 1 }}>{name}</div>
        {share != null && <div style={{ fontFamily: MONO, fontSize: 12, color: c, fontWeight: 600 }}>{share.toFixed(2)} %{shareBasis ? ` by ${shareBasis}` : ''}</div>}
      </div>
    </div>
  );
}

function SignalCell({ label, detail, primary, borderL }: { label: string; detail: { country: string; iso: string | null; case?: string | null; tier?: string | null; share_pct?: number | null; share_basis?: string | null; explanation?: string }; primary?: boolean; borderL?: boolean }) {
  return (
    <div style={{ padding: '14px 22px', borderLeft: borderL ? `1px solid #EFE9DD` : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: '#FFFFFF', background: primary ? C.red : C.mute, padding: '2px 7px', borderRadius: 3, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>{label.toUpperCase()}</span>
        {(detail.case || detail.tier) && (
          <span style={{ fontFamily: MONO, fontSize: 10, color: C.dim }}>{[detail.case && `case ${detail.case}`, detail.tier].filter(Boolean).join(' · ')}</span>
        )}
      </div>
      <CountryChip iso={detail.iso} name={detail.country} tier={detail.tier} share={detail.share_pct ?? null} shareBasis={detail.share_basis ?? null} />
      {detail.explanation && <div style={{ fontSize: 12, color: C.mute, marginTop: 8, lineHeight: 1.5 }}>{detail.explanation}</div>}
    </div>
  );
}

function SignalSummary({ apiOrigin }: { apiOrigin: ChronoApiOrigin }) {
  const p = apiOrigin.primary;
  const corr = apiOrigin.corroborating;
  if (!p) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: corr ? '1fr 1fr' : '1fr', gap: 0 }}>
      <SignalCell label="Primary API signal" detail={{ country: p.country, iso: p.iso, case: p.case, tier: p.tier, share_pct: p.share_pct, share_basis: p.share_basis, explanation: p.explanation }} primary />
      {corr && <SignalCell label="Corroborating signal" detail={{ country: corr.country, iso: corr.iso, share_pct: corr.share_pct, share_basis: corr.share_basis }} borderL />}
    </div>
  );
}

// ── Partner table ──────────────────────────────────────────────────────────
function EvidenceTable({ rows, accent }: { rows: EvidenceRow[]; accent: string }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: C.cream }}>
            {['Partner', 'Shipments', 'Weight (kg)', 'Value (USD)', '% weight', '% value', '% ships'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontFamily: MONO, fontSize: 9, fontWeight: 600, color: C.mute, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const isHi = !!r.highlight;
            const rowBg = isHi ? `${accent}0d` : (i % 2 ? C.soft : C.bg);
            return (
              <tr key={i} style={{ background: rowBg, borderBottom: `1px solid #EFE9DD` }}>
                <td style={{ padding: '8px 14px', color: C.ink, fontWeight: isHi ? 700 : 500 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Flag iso={r.partner_iso2} bordered={false} />
                    <span style={{ fontFamily: MONO, fontSize: 10, color: isHi ? accent : C.mute }}>{r.partner_iso2}</span>
                    <span>{r.partner}</span>
                    {isHi && <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: '#FFFFFF', background: accent, padding: '1px 5px', borderRadius: 2, letterSpacing: '0.08em', marginLeft: 4 }}>★</span>}
                  </span>
                  {r.note && <div style={{ fontFamily: MONO, fontSize: 10, color: C.dim, marginTop: 3, fontStyle: 'italic', lineHeight: 1.4 }}>↳ {r.note}</div>}
                </td>
                <td style={{ padding: '8px 14px', fontFamily: MONO, color: C.ink, verticalAlign: 'top' }}>{fmt(r.shipments)}</td>
                <td style={{ padding: '8px 14px', fontFamily: MONO, color: C.ink, verticalAlign: 'top' }}>{fmt(r.weight_kg)}</td>
                <td style={{ padding: '8px 14px', fontFamily: MONO, color: C.ink, verticalAlign: 'top' }}>{fmtUsd(r.value_usd)}</td>
                <td style={{ padding: '8px 14px', fontFamily: MONO, color: isHi ? accent : C.ink, fontWeight: isHi ? 700 : 400, verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
                    <div style={{ width: 50, height: 4, background: '#EFE9DD', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, r.sw)}%`, height: '100%', background: isHi ? accent : C.mute }} />
                    </div>
                    <span style={{ minWidth: 50 }}>{r.sw.toFixed(2)} %</span>
                  </div>
                </td>
                <td style={{ padding: '8px 14px', fontFamily: MONO, color: C.mute, verticalAlign: 'top' }}>{r.sv.toFixed(2)} %</td>
                <td style={{ padding: '8px 14px', fontFamily: MONO, color: C.mute, verticalAlign: 'top' }}>{r.ss.toFixed(2)} %</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ContextTable({ rows }: { rows: EvidenceRow[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: C.cream }}>
            {['Destination', 'Shipments', 'Weight (kg)', 'Value (USD)'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontFamily: MONO, fontSize: 9, fontWeight: 600, color: C.mute, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 ? C.soft : C.bg, borderBottom: `1px solid #EFE9DD` }}>
              <td style={{ padding: '8px 14px', color: C.ink, fontWeight: 500 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Flag iso={r.partner_iso2} bordered={false} />
                  <span style={{ fontFamily: MONO, fontSize: 10, color: C.mute }}>{r.partner_iso2}</span>
                  <span>{r.partner}</span>
                </span>
              </td>
              <td style={{ padding: '8px 14px', fontFamily: MONO, color: C.ink }}>{fmt(r.shipments)}</td>
              <td style={{ padding: '8px 14px', fontFamily: MONO, color: C.ink }}>{fmt(r.weight_kg)}</td>
              <td style={{ padding: '8px 14px', fontFamily: MONO, color: C.ink }}>{fmtUsd(r.value_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EvidencePanel({ panel, accent }: { panel: EvidencePanelModel; accent: string }) {
  const [open, setOpen] = React.useState(false);
  const empty = !panel.rows || panel.rows.length === 0;

  if (empty) {
    return (
      <div style={{ background: C.soft, border: `1px dashed ${C.border}`, borderRadius: 8, padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: C.dim, letterSpacing: '0.08em' }}>{panel.label}</span>
          {panel.sublabel && <span style={{ fontFamily: MONO, fontSize: 9, color: C.dim }}>· {panel.sublabel}</span>}
        </div>
        <div style={{ fontSize: 12, color: C.mute, fontStyle: 'italic', lineHeight: 1.45 }}>{panel.interpretation}</div>
      </div>
    );
  }

  const isPrimary = !!panel.primary;
  const isContext = !!panel.contextOnly;
  const borderColor = isPrimary ? accent : C.border;

  return (
    <div style={{ background: C.bg, border: `${isPrimary ? 2 : 1}px solid ${borderColor}`, borderRadius: 8, overflow: 'hidden', boxShadow: isPrimary ? `0 4px 14px ${accent}1f` : 'none' }}>
      <button onClick={() => setOpen(o => !o)} style={{ all: 'unset', cursor: 'pointer', width: '100%', display: 'block', boxSizing: 'border-box' }}>
        <div style={{ padding: '10px 14px', display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 14, alignItems: 'center', background: open ? C.soft : C.bg, borderBottom: open ? `1px solid ${C.line}` : 'none' }}>
          <div>
            {isPrimary && <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: '#FFFFFF', background: accent, padding: '2px 7px', borderRadius: 3, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>PRIMARY API SIGNAL</span>}
            {isContext && <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: '#FFFFFF', background: C.mute, padding: '2px 7px', borderRadius: 3, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>CONTEXT</span>}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{panel.label}</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{panel.sublabel}</div>
          </div>
          <span style={{ fontFamily: MONO, fontSize: 10, color: C.mute, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{open ? 'HIDE' : 'SHOW'} {panel.rows.length} ROW{panel.rows.length !== 1 ? 'S' : ''} ▾</span>
        </div>
      </button>
      {open && (
        <div style={{ animation: 'chrSlideUp 0.2s ease' }}>
          {isContext ? <ContextTable rows={panel.rows} /> : <EvidenceTable rows={panel.rows} accent={accent} />}
          <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.line}`, background: C.soft, fontSize: 12, color: C.ink, fontStyle: 'italic', lineHeight: 1.5 }}>
            <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: C.mute, letterSpacing: '0.12em', fontStyle: 'normal', marginRight: 10 }}>INTERPRETATION</span>
            {panel.interpretation}
          </div>
        </div>
      )}
    </div>
  );
}

function EvidenceGroup({ title, desc, color, panels, summary }: { title: string; desc: string; color: string; panels: Record<string, EvidencePanelModel>; summary?: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const entries = Object.entries(panels);
  if (entries.length === 0) return null;
  const totalRows = entries.reduce((s, [, p]) => s + (p.rows?.length || 0), 0);

  return (
    <div style={{ borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden', background: C.bg }}>
      <button onClick={() => setOpen(o => !o)} style={{ all: 'unset', cursor: 'pointer', width: '100%', padding: '12px 18px', background: open ? C.soft : C.bg, display: 'flex', alignItems: 'center', gap: 12, borderBottom: open ? `1px solid ${C.line}` : 'none', boxSizing: 'border-box' }}>
        <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: '0.14em' }}>{title}</span>
        <span style={{ width: 4, height: 4, background: color, borderRadius: 999 }} />
        <span style={{ fontSize: 12, color: C.mute }}>{desc}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.dim, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{entries.length} PANEL{entries.length !== 1 ? 'S' : ''} · {totalRows} ROWS</span>
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" style={{ color: C.mute, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease', flexShrink: 0 }}>
          <path d="M3 5 L7 9 L11 5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div style={{ background: C.soft }}>
          {summary && <div style={{ borderBottom: `1px solid ${C.line}`, background: C.bg }}>{summary}</div>}
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {entries.map(([key, p]) => <EvidencePanel key={key} panel={p} accent={color} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EvidenceStack({ groups, apiOrigin }: { groups: EvidenceGroups; apiOrigin: ChronoApiOrigin }) {
  const hasCoo = Object.keys(groups.coo_confirmation).length > 0;
  const hasApi = Object.keys(groups.api_origin_signals).length > 0;
  if (!hasCoo && !hasApi) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {hasCoo && <EvidenceGroup title={GROUP_COO.label} desc={GROUP_COO.desc} color={GROUP_COO.color} panels={groups.coo_confirmation} />}
      {hasApi && <EvidenceGroup title={GROUP_API.label} desc={GROUP_API.desc} color={GROUP_API.color} panels={groups.api_origin_signals} summary={<SignalSummary apiOrigin={apiOrigin} />} />}
    </div>
  );
}
